import React from 'react';
import PropTypes from 'prop-types';
import { FlexGrid, Container } from '@gasbuddy/react-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faCheck, faTimes, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

import styles from './styles.css';
import { convertStatusToDisplayName, JOBS, getSentimentDisplayValue } from '../utils';
import {
  JobPropTypes,
  RatingCategoryListPropTypes,
  StationListPropTypes,
  StationListsPropTypes,
  BasicStationPropTypes,
  BasicStationsPropTypes,
  BrandPropTypes,
} from '../prop-types';
import { ComparisonMetricTypes } from '../../../../lib/constants/enums';

function Job({ job, onEdit, ratingCategories, stationList, station, brand, brands }) {
  const handleOnEditClick = () => {
    onEdit(job);
  };

  const renderJobParameter = (parameter, title, formatter = v => v) => (
    <>
      {parameter ? (
        <div>
          <b>{`${title}: `}</b>
          {Array.isArray(parameter) ? parameter.map(p => (formatter(p))).join(', ') : formatter(parameter)}
        </div>
      ) : null}
    </>

  );

  const renderReviewKeywordParameters = () => (
    <>
      {job.parameters.sentiment_filter && renderJobParameter(job.parameters.sentiment_filter, 'Has Sentiment', getSentimentDisplayValue)}
      {job.parameters.keywords && renderJobParameter(job.parameters.keywords, 'Selected Keywords')}
    </>
  );

  const getRatingCategoryName = v => ratingCategories.find((rc => rc.id.toString() === v.toString())).name;

  const renderRatingCategoryScoreChangeParameters = () => (
    <>
      {job.parameters.category_ids && renderJobParameter(
        job.parameters.category_ids, 'Rating Categories', getRatingCategoryName,
      )}
      {job.parameters.change_from && renderJobParameter(job.parameters.change_from, 'Status From', convertStatusToDisplayName)}
      {job.parameters.change_to && renderJobParameter(job.parameters.change_to, 'Status To', convertStatusToDisplayName)}
    </>
  );

  const renderBasePDFParameters = () => (
    <>
      {job.parameters.comparison && renderJobParameter(job.parameters.comparison, 'Compare To', displayComparison)}
      {job.parameters.recipients && renderJobParameter(job.parameters.recipients, 'Recipients')}
      {job.parameters.comments && renderJobParameter(job.parameters.comments, 'Comments')}

    </>
  );

  const renderStationRatingsPDFParameters = () => (
    <>
      {job.parameters.category_id && renderJobParameter(job.parameters.category_id, 'Rating Category', getRatingCategoryName)}
      {renderBasePDFParameters()}
    </>
  );

  const getBrandDisplayName = value => brands.find(b => b.id.toString() === value)?.display_name || '';

  const renderCompetitorBenchmarkParameters = () => (
    <>
      {job.parameters.brand_1 && renderJobParameter(job.parameters.brand_1, 'Brand 1', getBrandDisplayName)}
      {job.parameters.brand_2 && renderJobParameter(job.parameters.brand_2, 'Brand 2', getBrandDisplayName)}
      {job.parameters.brand_3 && renderJobParameter(job.parameters.brand_3, 'Brand 3', getBrandDisplayName)}
      {renderBasePDFParameters()}
    </>
  );

  const displayComparison = ({ type }) => `${getComparisonTypeDisplayValue(type)}${getComparisonTargetDisplayValue(type)}`;

  const getComparisonTypeDisplayValue = (type) => {
    switch (type) {
      case 'nearby':
        return ComparisonMetricTypes.nearbyStations;
      case 'industry':
        return ComparisonMetricTypes.industry;
      case 'brands':
        return ComparisonMetricTypes.brand;
      case 'station-lists':
        return ComparisonMetricTypes.myStations;
      case 'prior-period':
      default:
        return ComparisonMetricTypes.priorPeriod;
    }
  };

  const getComparisonTargetDisplayValue = (type) => {
    switch (type) {
      case 'brands':
        return `: ${brand.display_name}`;
      default:
        return '';
    }
  };

  const renderJobParameters = () => {
    switch (job.job_type.name) {
      case JOBS.REVIEW_KEYWORD:
        return renderReviewKeywordParameters();
      case JOBS.RATING_CATEGORY_SCORE_CHANGE:
        return renderRatingCategoryScoreChangeParameters();
      case JOBS.STATION_PROFILE_PDF:
      case JOBS.STATION_REVIEWS_PDF:
      case JOBS.BRAND_ENGAGEMENT_PDF:
        return renderBasePDFParameters();
      case JOBS.STATION_RATINGS_PDF:
        return renderStationRatingsPDFParameters();
      case JOBS.COMPETITOR_BENCHMARK_PDF:
        return renderCompetitorBenchmarkParameters();
      default:
        return null;
    }
  };

  return (
    <Container className={styles.alertContainer}>
      <div className={styles.alertContent}>
        <FlexGrid>
          <FlexGrid.Column mobile={9} className={styles.alertDetails}>
            <FlexGrid>
              <FlexGrid.Column mobile={9} className={styles.alertInfo}>
                <div>
                  <b>Report Name: </b>
                  {job.display_name}
                </div>
                <div>
                  <b>Report Type: </b>
                  {job.job_type.display_name}
                </div>
                {renderJobParameters()}
              </FlexGrid.Column>
              <FlexGrid.Column className={styles.alertStatus} mobile={3}>
                <div className={styles.statusContainer}>
                  <div>
                    <button type="button" onClick={handleOnEditClick} className={`${styles.faIcon} ${styles.faButton} ${styles.faIconBlue}`}>
                      <FontAwesomeIcon icon={faPencilAlt} />
                    </button>
                  </div>
                  <div>
                    <div className={`${styles.faIcon} ${job.enabled ? styles.faIconGreen : styles.faIconRed}`}>
                      <FontAwesomeIcon icon={job.enabled ? faCheck : faTimes} />
                    </div>
                    <span>{` Report ${job.enabled ? 'Enabled' : 'Disabled'}`}</span>
                  </div>
                </div>
              </FlexGrid.Column>
            </FlexGrid>
          </FlexGrid.Column>
          <FlexGrid.Column mobile={3} className={styles.affectedStations}>
            {stationList ? (
              <>
                <h5>Selected Station List</h5>
                <div className={styles.stationName}>{stationList.name}</div>
                <div>{`${stationList.station_count} stations`}</div>
              </>
            ) : null}
            { job.parameters.station_list_id && !stationList
              ? (
                <FlexGrid.Column className={styles.bottomMargin}>
                  <FlexGrid>
                    <FlexGrid.Column mobile={2}>
                      <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />
                    </FlexGrid.Column>
                    <FlexGrid.Column mobile={10}>
                      <b>Selected Station List has been deleted. Please update or delete this report.</b>
                    </FlexGrid.Column>
                  </FlexGrid>
                </FlexGrid.Column>
              ) : null }
            {station ? (
              <>
                <h5>Selected Station</h5>
                <div className={styles.stationName}>{`#${station.retailer_reference} ${station.retailer_name}`}</div>
              </>
            ) : null}
          </FlexGrid.Column>
        </FlexGrid>
      </div>
    </Container>
  );
}

Job.propTypes = {
  job: JobPropTypes.isRequired,
  onEdit: PropTypes.func,
  ratingCategories: RatingCategoryListPropTypes.isRequired,
  stationList: StationListPropTypes,
  station: BasicStationPropTypes,
  brand: BrandPropTypes,
  brands: PropTypes.arrayOf(BrandPropTypes),
};

Job.defaultProps = {
  onEdit: () => { },
  stationList: null,
  station: null,
  brand: null,
  brands: [],
};

function JobList({ jobs, onEdit, ratingCategories, stationLists, stations, brands }) {
  return (
    <div>
      {jobs.map(job => (
        <Job
          key={job.id}
          job={job}
          onEdit={onEdit}
          ratingCategories={ratingCategories}
          stationList={stationLists.find(sl => sl.id === job.parameters.station_list_id)}
          station={stations.find(s => s.id === job.parameters.station_id)}
          brand={(job.parameters.comparison && job.parameters.comparison.type === 'brands')
            ? brands.find(b => b.id === Number(job.parameters.comparison.target)) : null}
          brands={brands}
        />
      ))}
    </div>
  );
}

JobList.propTypes = {
  jobs: PropTypes.arrayOf(JobPropTypes),
  onEdit: PropTypes.func,
  ratingCategories: RatingCategoryListPropTypes.isRequired,
  stationLists: StationListsPropTypes.isRequired,
  stations: BasicStationsPropTypes.isRequired,
  brands: PropTypes.arrayOf(BrandPropTypes).isRequired,
};

JobList.defaultProps = {
  jobs: [],
  onEdit: () => { },
};

export default JobList;
